/**
 * Phase F-finale-7 Slice 7b.4c — IA fixed-mode allocation API client.
 *
 * Wraps the real-auth seating *seat-assignment* surface (eduviaos-backend
 * `app/domains/seating/routes.py`, all `manage_zones`-guarded):
 *
 *   GET    /api/seat-assignments              (list, student/seat/zone/status filters + paging)
 *   POST   /api/seat-assignments              (assign a student to a seat — 201)
 *   DELETE /api/seat-assignments/{id}         (release; requires a reason body)
 *
 * A "seat assignment" is the fixed-mode *permanent* allocation of a seat to
 * a student — distinct from a dynamic per-shift `Booking` (Slice 7b.4d).
 * Tenant + actor (`assigned_by`/`released_by`) are resolved BE-side from the
 * JWT `AuthContext`; none are client-trusted. Reads + writes both require
 * `MANAGE_ZONES` (seating `_ADMIN_GUARD`); the FE additionally gates write
 * affordances via `useCanEditSeating`, the same hook Zones/Seats use.
 *
 * The wire shapes mirror `app/domains/seating/schemas.py`
 * (`SeatAssignmentResponse`, `CreateSeatAssignmentRequest`,
 * `ReleaseSeatAssignmentRequest`, `SeatAssignmentListResponse`) exactly —
 * any field-shape change on the BE must land here too (Iron Rule #1/#11).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiError } from "@eduviaos/shared";
import { api } from "@/lib/api";

/** A seat assignment's lifecycle status (BE `SeatAssignmentStatusLiteral`). */
export type SeatAssignmentStatus = "active" | "released" | "expired";

/** How the assignment was created (BE `AssignmentMethodLiteral`). Admin
 *  assignment through this surface always writes `admin_assigns`. */
export type AssignmentMethod = "admin_assigns" | "student_picks" | "auto_round_robin";

/** Mirror of `SeatAssignmentResponse`. */
export interface SeatAssignment {
  id: string;
  tenant_id: string;
  branch_id: string | null;
  student_id: string;
  seat_id: string;
  zone_id: string | null;
  assignment_method: string;
  status: string;
  starts_at: string;
  ends_at: string | null;
  assigned_by: string | null;
  released_at: string | null;
  released_by: string | null;
  release_reason: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface SeatAssignmentListResponse {
  items: SeatAssignment[];
  total: number;
}

/** Mirror of `CreateSeatAssignmentRequest`. `starts_at`/`ends_at` are ISO
 *  strings; both optional (BE defaults `starts_at` to now, `ends_at` open). */
export interface CreateSeatAssignmentInput {
  student_id: string;
  seat_id: string;
  branch_id?: string | null;
  starts_at?: string | null;
  ends_at?: string | null;
  metadata?: Record<string, unknown>;
}

/** Mirror of `ReleaseSeatAssignmentRequest` — the reason is mandatory
 *  (1..2000 chars) and recorded as `release_reason` for the audit trail. */
export interface ReleaseSeatAssignmentInput {
  reason: string;
}

export interface SeatAssignmentListFilters {
  student_id?: string;
  seat_id?: string;
  zone_id?: string;
  status?: SeatAssignmentStatus;
  branch_id?: string;
  limit?: number;
  offset?: number;
}

// ── React Query hooks ─────────────────────────────────────────────────

export const seatAssignmentKeys = {
  all: ["ia", "seating", "seat-assignments"] as const,
  list: (filters?: SeatAssignmentListFilters) =>
    [...seatAssignmentKeys.all, "list", filters ?? {}] as const,
};

function buildAssignmentsQuery(filters?: SeatAssignmentListFilters): string {
  const search = new URLSearchParams();
  if (!filters) return "";
  if (filters.student_id) search.set("student_id", filters.student_id);
  if (filters.seat_id) search.set("seat_id", filters.seat_id);
  if (filters.zone_id) search.set("zone_id", filters.zone_id);
  if (filters.status) search.set("status", filters.status);
  if (filters.branch_id) search.set("branch_id", filters.branch_id);
  if (filters.limit !== undefined) search.set("limit", String(filters.limit));
  if (filters.offset !== undefined) search.set("offset", String(filters.offset));
  return search.toString();
}

export function useSeatAssignments(filters?: SeatAssignmentListFilters) {
  const qs = buildAssignmentsQuery(filters);
  return useQuery<SeatAssignmentListResponse>({
    queryKey: seatAssignmentKeys.list(filters),
    queryFn: () =>
      api.get<SeatAssignmentListResponse>(`/api/seat-assignments${qs ? `?${qs}` : ""}`),
    staleTime: 15_000,
  });
}

export function useCreateSeatAssignment() {
  const qc = useQueryClient();
  return useMutation<SeatAssignment, ApiError, CreateSeatAssignmentInput>({
    mutationKey: ["ia", "seating", "seat-assignments", "create"],
    mutationFn: (body) => api.post<SeatAssignment>("/api/seat-assignments", body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: seatAssignmentKeys.all });
    },
  });
}

export interface ReleaseSeatAssignmentVars {
  id: string;
  body: ReleaseSeatAssignmentInput;
}

export function useReleaseSeatAssignment() {
  const qc = useQueryClient();
  return useMutation<SeatAssignment, ApiError, ReleaseSeatAssignmentVars>({
    mutationKey: ["ia", "seating", "seat-assignments", "release"],
    // DELETE carries the mandatory `reason` in the request body (axios
    // `config.data`), matching `ReleaseSeatAssignmentRequest`. The BE returns
    // the released assignment (status → `released`), not 204.
    mutationFn: ({ id, body }) =>
      api.delete<SeatAssignment>(`/api/seat-assignments/${id}`, { data: body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: seatAssignmentKeys.all });
    },
  });
}
